import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Renders an AI clinical report (Markdown) as clean, well-typed HTML.
 * Used on-screen and for print/PDF. Uses the app's Inter font with a clear
 * clinical hierarchy; print styling lives under `.report-markdown` in index.css.
 */
export default function ReportMarkdown({ content }: { content: string }) {
  return (
    <div className="report-markdown text-[15px] leading-relaxed text-slate-700">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children }) => (
            <h1 className="mt-1 mb-3 text-2xl font-bold tracking-tight text-slate-900">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mt-6 mb-2 border-b border-slate-200 pb-1.5 text-lg font-semibold tracking-tight text-slate-900">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mt-4 mb-1.5 text-[13px] font-semibold uppercase tracking-wide text-purple-700">{children}</h3>
          ),
          p: ({ children }) => <p className="my-2.5 leading-7">{children}</p>,
          ul: ({ children }) => (
            <ul className="my-2.5 list-disc space-y-1 pl-5 marker:text-purple-400">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="my-2.5 list-decimal space-y-1 pl-5 marker:text-purple-400">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-7">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-slate-900">{children}</strong>,
          em: ({ children }) => <em className="italic text-slate-600">{children}</em>,
          hr: () => <hr className="my-5 border-slate-200" />,
          blockquote: ({ children }) => (
            <blockquote className="my-3 rounded-r border-l-4 border-purple-300 bg-purple-50/60 py-1.5 pl-4 italic text-slate-600">
              {children}
            </blockquote>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="font-medium text-purple-700 underline decoration-purple-300 underline-offset-2 hover:text-purple-800"
            >
              {children}
            </a>
          ),
          code: ({ children }) => (
            <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[13px] text-slate-800">{children}</code>
          ),
          table: ({ children }) => (
            <div className="my-3 overflow-x-auto">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-slate-50">{children}</thead>,
          th: ({ children }) => (
            <th className="border border-slate-200 px-3 py-2 text-left font-semibold text-slate-700">{children}</th>
          ),
          td: ({ children }) => <td className="border border-slate-200 px-3 py-2 align-top">{children}</td>,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
